import React, { useState } from "react";
import { recheckAhaSending } from "../../../api/globalAdminApi";
import { Notice, Panel, StatusChip } from "../ui/AdminUi";
import { I } from "../ui/icons";

const STATE_LABEL = { PENDING: "Queued", SENDING: "Sending", UNKNOWN: "Result unknown" };
const CHIP = { ok: "OK", warning: "Warn", degraded: "Degraded" };

/** "3 min", "2 h", "1 d" for an age in seconds. */
export function formatAge(seconds) {
  if (seconds == null) return "—";
  if (seconds < 90) return `${Math.max(0, Math.round(seconds))} s`;
  if (seconds < 5400) return `${Math.round(seconds / 60)} min`;
  if (seconds < 129600) return `${Math.round(seconds / 3600)} h`;
  return `${Math.round(seconds / 86400)} d`;
}

function JobLine({ label, run }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "6px 0" }}>
      <span>{label}</span>
      {run ? (
        <span className="sub" style={{ textAlign: "right" }}>
          <StatusChip status={run.ok ? "OK" : "Failed"} /> {run.detail}
          <div className="mono" style={{ fontSize: 12 }}>{new Date(run.at).toLocaleTimeString()}</div>
        </span>
      ) : (
        <span className="sub">No run recorded since this server started</span>
      )}
    </div>
  );
}

/**
 * Whether AHA eCard sending is moving: how many requests wait in each state and for how long, what the background
 * sender and recovery job last did, and the unsettled requests. Shows no student data. Staff can re-run recovery.
 */
export default function AhaHealthPanel({ aha, onRefreshed }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  if (!aha || !aha.enabled) return null;

  const counts = aha.counts || {};
  const stuck = aha.stuck || [];
  const issues = aha.issues || [];

  const recheck = () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    recheckAhaSending()
      .then((d) => {
        setMessage(d.interrupted > 0
          ? `${d.interrupted} stuck request(s) were marked for review.`
          : "Recovery ran. Nothing was stuck long enough to move.");
        if (onRefreshed) onRefreshed(d.aha);
      })
      .catch((err) => setError(err?.message || "The re-check could not be run."))
      .finally(() => setBusy(false));
  };

  return (
    <Panel
      title="AHA eCard sending"
      subtitle={`${aha.environment || "No environment"}${aha.environmentOverride ? " (test override: cards are AHA QA test cards)" : ""} · dispatch ${aha.dispatchEnabled ? "on" : "off"} · sender ${aha.workerEnabled ? "on" : "off"}`}
      actions={<button type="button" className="b sec" onClick={recheck} disabled={busy}><I.refresh s={14} />{busy ? "Checking…" : "Re-check now"}</button>}
      style={{ marginBottom: 22 }}
    >
      <div className="panel-b" style={{ display: "grid", gap: 14 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <StatusChip status={CHIP[aha.status] || "Unknown"} />
          <b style={{ fontWeight: 600 }}>{aha.summary}</b>
        </div>
        {issues.length > 1 && (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {issues.slice(1).map((text) => <li key={text}>{text}</li>)}
          </ul>
        )}
        {message && <Notice kind="info">{message}</Notice>}
        {error && <Notice kind="bad">{error}</Notice>}

        <div style={{ display: "flex", gap: 22, flexWrap: "wrap" }}>
          {["PENDING", "SENDING", "UNKNOWN", "ASSIGNED", "REJECTED"].map((state) => (
            <div key={state}>
              <div className="mono" style={{ fontSize: 22, fontWeight: 600 }}>{counts[state] ?? 0}</div>
              <div className="sub">{STATE_LABEL[state] || (state === "ASSIGNED" ? "Issued" : "Rejected")}</div>
            </div>
          ))}
          <div>
            <div className="mono" style={{ fontSize: 22, fontWeight: 600 }}>{formatAge(aha.oldestPendingSeconds)}</div>
            <div className="sub">Oldest queued</div>
          </div>
          <div>
            <div className="mono" style={{ fontSize: 22, fontWeight: 600 }}>{formatAge(aha.oldestSendingSeconds)}</div>
            <div className="sub">Oldest sending</div>
          </div>
        </div>

        <div>
          <JobLine label="Background sender" run={aha.worker} />
          <JobLine label="Stuck-request recovery" run={aha.recovery} />
          <JobLine label="Last call to AHA" run={aha.lastProviderCall} />
        </div>

        {stuck.length > 0 && (
          <div className="tw">
            <table className="tbl" style={{ minWidth: 0 }}>
              <thead><tr><th>Request</th><th>Class</th><th>State</th><th>Reason</th><th className="r">Waiting</th></tr></thead>
              <tbody>
                {stuck.map((row) => (
                  <tr key={row.id}>
                    <td className="mono">#{row.id}</td>
                    <td className="mono">{row.classId}</td>
                    <td>{STATE_LABEL[row.state] || row.state}</td>
                    <td className="sub">{row.errorCode || "—"}</td>
                    <td className="r mono">{formatAge(row.ageSeconds)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Panel>
  );
}
